import { useNotification, useTranslation } from '@topcon/topcon-utils';
import { Statuses } from '@topcon/tuicl';

import {
  ServiceAgreementTermsEnum,
  ServiceAgreementWithCustomerPostBody,
  ServiceAgreementBody,
  BusinessPartnerBody,
  ServiceAgreementWithProviderPostBody,
} from '../api/generated/pm';
import {
  useCreateServiceAgreementWithCustomer,
  useDeleteServiceAgreement,
} from '../pages/Customers/customers.mutations';
import { invalidateCustomerAgreementLists } from '../pages/Customers/customers.queries';
import { useCreateServiceAgreementWithProvider } from '../pages/Settings/Dealers/dealers.mutations';
import { invalidateServiceAgreementsWithProvider } from '../pages/Settings/Dealers/dealers.queries';
import { useErrorDrawer } from '../providers/ErrorDrawerContext/error-drawer.context';

function useServiceAgreement() {
  const {
    mutateAsync: createWithCustomerAsync,
    isPending: isPendingCreateServiceAgreementWithCustomer,
  } = useCreateServiceAgreementWithCustomer();

  const {
    mutateAsync: createWithProviderAsync,
    isPending: isPendingCreateServiceAgreementWithProvider,
  } = useCreateServiceAgreementWithProvider();

  const { mutateAsync: deleteServiceAgreementAsync, isPending: isPendingCancelServiceAgreement } =
    useDeleteServiceAgreement();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { t } = useTranslation();

  const createServiceAgreementWithCustomer = async (
    payload: ServiceAgreementWithCustomerPostBody,
    customerName: string
  ) => {
    try {
      const res = await createWithCustomerAsync(payload);
      return res;
    } catch (error: unknown) {
      if (!showError(error)) {
        notificationService.addNotification(
          t('customers.createModal.messages.createCustomerErrorTitle'),
          t('customers.createModal.messages.createCustomerErrorDescription', {
            name: customerName,
          }),
          Statuses.error
        );
      }
      return Promise.reject(error);
    }
  };

  const createServiceAgreementWithProvider = async (
    provider: BusinessPartnerBody,
    agreementType: ServiceAgreementTermsEnum
  ) => {
    try {
      const payload: ServiceAgreementWithProviderPostBody = {
        serviceAgreementTerms: agreementType,
        providerBusinessPartnerId: provider.id,
      };
      return createWithProviderAsync(payload);
    } catch (error: unknown) {
      return Promise.reject(error);
    }
  };

  const deleteServiceAgreement = async (serviceAgreement: ServiceAgreementBody) => {
    try {
      await deleteServiceAgreementAsync(serviceAgreement.id);

      invalidateServiceAgreementsWithProvider();
      invalidateCustomerAgreementLists();

      return true;
    } catch (error: unknown) {
      if (!showError(error)) {
        notificationService.addNotification(
          t('terminateAgreementModal.messages.errorRequestTitle'),
          t('terminateAgreementModal.messages.errorRequestDescription'),
          Statuses.error
        );
      }
      return Promise.reject(error);
    }
  };

  return {
    createServiceAgreementWithCustomer,
    isPendingCreateServiceAgreementWithCustomer,
    createServiceAgreementWithProvider,
    isPendingCreateServiceAgreementWithProvider,
    deleteServiceAgreement,
    isPendingCancelServiceAgreement,
  };
}

export default useServiceAgreement;
